import React, {useState, useEffect, useCallback} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Tippy from "@tippyjs/react";
import { tippyProps as defaultTippyProps } from "../../helpers/tippyOptions";
import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import debounce from "lodash/debounce";

import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light-border.css";
import "./PasswordTippy.css";

interface PasswordTippyProps extends React.PropsWithChildren {
    password: string;
    validatePasswordRequirements: (password: string) => Promise<string>;
    passwordValid: boolean;
    setPasswordValid: (valid: boolean) => void;
    requirementsTitle: string;
    requirementLengthText: string;
    requirementClassesText: string;
    children: React.ReactElement;
}

const PasswordTippy: React.FC<PasswordTippyProps> = ({
    password,
    validatePasswordRequirements,
    passwordValid,
    setPasswordValid,
    requirementsTitle,
    requirementLengthText,
    requirementClassesText,
    children
 }) => {

    const [passwordValidationError, setPasswordValidationError] = useState("");
    const [passwordLengthValid, setPasswordLengthValid] = useState(false);
    const [passwordMinClassValid, setPasswordMinClassValid] = useState(false);
    const [tippyProps, setTippyProps] = useState({});

    const isPasswordLengthValid = (pw: string) => {
        const valid = pw.length >= 8 && pw.length <= 64;
        setPasswordLengthValid(valid);
        return valid;
    };

    const doesPasswordContainMinClasses = (pw: string) => {
        let count = 0;
        if (pw.match("[a-z]+")) count++;
        if (pw.match("[A-Z]+")) count++;
        if (pw.match("[\\d]+")) count++;
        if (pw.match("[\\[\\]!@#$%^&*_+]+")) count++;
        const valid = count >= 3;
        setPasswordMinClassValid(valid);
        return valid;
    };

    const isPasswordApiCheckValid = async (pw: string) => {
        const error = await validatePasswordRequirements(pw);
        setPasswordValidationError(error);
        return !error;
    };

    const checkPasswordValidity = useCallback(
        debounce(async (pw) => {
            const c1 = isPasswordLengthValid(pw);
            const c2 = doesPasswordContainMinClasses(pw);
            setPasswordValid(
                c1 && c2 && (await isPasswordApiCheckValid(pw)),
            );
        }, 500),
        [],
    );

    const onWindowResize = useCallback(
        debounce(() => {
            if (document.body.clientWidth > 800) {
                setTippyProps({
                    ...defaultTippyProps,
                    allowHTML: true,
                    placement: "right",
                    offset: [0, 50],
                });
            } else {
                setTippyProps({
                    ...defaultTippyProps,
                    allowHTML: true,
                    placement: "top",
                    offset: [80, 7],
                });
            }
        }, 300),
        [],
    );

    useEffect(() => {
        checkPasswordValidity(password);
        return () => {
            checkPasswordValidity.cancel();
        };
    }, [password]);

    useEffect(() => {
        window.addEventListener("resize", onWindowResize);
        onWindowResize();
        return () => {
            window.removeEventListener("resize", onWindowResize);
            onWindowResize.cancel();
        };
    }, []);

    const isTippyVisible = () => !!password.length && !passwordValid;

    const passwordRequirements = (
        <div id="password-requirements">
            <div className="requirements-title">
                <span>{requirementsTitle}</span>
            </div>
            <div className={passwordLengthValid ? "valid" : "invalid"}>
                {passwordLengthValid && <FontAwesomeIcon icon={faCircleCheck}/>}
                {!passwordLengthValid && <FontAwesomeIcon icon={faCircleXmark}/>}
                &nbsp;
                <span className="requirement-text"
                      dangerouslySetInnerHTML={{
                          __html: requirementLengthText,
                      }}
                />
            </div>
            <div className={passwordMinClassValid ? "valid" : "invalid"}>
                {passwordMinClassValid && <FontAwesomeIcon icon={faCircleCheck}/>}
                {!passwordMinClassValid && <FontAwesomeIcon icon={faCircleXmark}/>}
                &nbsp;
                <span className="requirement-text"
                      dangerouslySetInnerHTML={{
                          __html: requirementClassesText,
                      }}
                />
            </div>
            {passwordLengthValid && passwordMinClassValid && passwordValidationError && (
                <div className="invalid">
                    <FontAwesomeIcon icon={faCircleXmark}/>
                    &nbsp;
                    <span className="requirement-text">{passwordValidationError}</span>
                </div>
            )}
        </div>
    );

    return <>
        <Tippy {...tippyProps} content={passwordRequirements} visible={isTippyVisible()}>
            {children}
        </Tippy>
    </>;
};

export default PasswordTippy;
